import api from "./api";
import { exclusiveSkipsConvergence } from "../utils/gatewayConvergence";

// ========== GATEWAY TYPE CONSTANTS ==========
/**
 * Gateway types supported by the BPMN editor
 * @enum {string}
 */
export const GatewayType = {
  /** Only ONE path is taken based on condition (XOR) */
  EXCLUSIVE: "EXCLUSIVE",
  /** ALL paths execute simultaneously (AND) */
  PARALLEL: "PARALLEL",
  /** One or more paths based on conditions (OR) */
  INCLUSIVE: "INCLUSIVE",
  /** Wait for an external event */
  EVENT_BASED: "EVENT_BASED",
  /** Complex evaluation semantics */
  COMPLEX: "COMPLEX",
};

const normalizeType = (t) => (t || "").toString().toUpperCase();
const START_EVENT_GATEWAY_ID = "__START_EVENT__";
const toGatewayTaskId = (value) =>
  String(value) === START_EVENT_GATEWAY_ID
    ? null
    : value != null && value !== ""
      ? Number(value)
      : null;

const isConvergingType = (type) => {
  const normalized = normalizeType(type);
  return (
    normalized === "PARALLEL" ||
    normalized === "INCLUSIVE" ||
    normalized === "EXCLUSIVE" ||
    normalized === "COMPLEX"
  );
};

const firstNonEmpty = (...vals) => {
  for (const v of vals) {
    if (v != null && String(v) !== "") return String(v);
  }
  return "";
};

/**
 * Split the backend's flat gateway-branch array into the editor's two buckets:
 * regular branches (to a task or the default End) and endpoints (named/specific
 * end events). A branch is an endpoint when it carries an endEventName or
 * endTaskId and has no target task. The default "→ End" branch (no target, no
 * end-event name) stays a regular branch so it survives a re-edit round-trip.
 * Accepts both camelCase (gateway endpoint) and snake_case (raw process row)
 * field names.
 * @param {Array} rawBranches
 * @returns {{ branches: Array, endpoints: Array }}
 */
export function splitBranchesAndEndpoints(rawBranches) {
  const branches = [];
  const endpoints = [];
  (rawBranches || []).forEach((b) => {
    const id = b.branch_pk_id ?? b.id;
    let targetTaskId = firstNonEmpty(b.targetTaskId, b.target_task_id);
    // Branch → gateway: the backend stores target_gateway_id. Encode it as
    // "__GW__<gatewayId>" in targetTaskId (mirrors the converge dropdown) so the
    // editor lists the gateway and the choice round-trips on re-edit.
    const targetGatewayId = firstNonEmpty(b.targetGatewayId, b.target_gateway_id);
    if (
      (targetTaskId === "" || targetTaskId == null) &&
      targetGatewayId !== "" &&
      targetGatewayId != null
    ) {
      targetTaskId = `__GW__${targetGatewayId}`;
    }
    const endEventName = b.endEventName || b.end_event_name || "";
    const endTaskId = firstNonEmpty(b.endTaskId, b.end_task_id);
    const condition = b.condition || "";
    // A named End event now lives ON the branch (target = End Event + a name),
    // so it stays a branch and round-trips as one. Only the rare "end after a
    // specific task" (endTaskId with no name) remains a legacy endpoint.
    const isEndpoint =
      targetTaskId === "" && endTaskId !== "" && endEventName.trim() === "";
    if (isEndpoint) {
      endpoints.push({ id, endTaskId, endEventName, condition });
    } else {
      branches.push({
        id,
        targetTaskId,
        endEventName,
        condition,
        isDefault: !!(b.isDefault ?? b.is_default),
        connectToEnd: (b.connectToEnd ?? b.connect_to_end) !== false,
      });
    }
  });
  return { branches, endpoints };
}

const toConvergeAtTaskId = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

function normalizeGatewayPayload(gateway) {
  const payload = {
    type: normalizeType(gateway?.type || "EXCLUSIVE"),
    name: gateway?.name || undefined,
    afterTaskId: toGatewayTaskId(gateway?.afterTaskId),
    branches: Array.isArray(gateway?.branches) ? gateway.branches : [],
  };

  if (isConvergingType(payload.type) && !exclusiveSkipsConvergence(gateway)) {
    payload.convergeToEnd = !!gateway?.convergeToEnd;
    payload.convergeAtTaskId =
      !payload.convergeToEnd && !gateway?.convergeAtGatewayId
        ? toConvergeAtTaskId(gateway?.convergeAtTaskId)
        : null;
    payload.convergeGatewayName =
      String(gateway?.convergeGatewayName || "").trim() || null;
  }

  return payload;
}

// ========== UTILITY FUNCTIONS ==========

/**
 * Transform gateways to backend format for saving
 * Handles both branches and endpoints from the frontend GatewaySection component
 * @param {Array} gateways - Frontend gateway array
 * @returns {Array} Backend-formatted gateway array
 */
export function transformGatewaysForSave(gateways) {
  return (gateways || []).map((g) => {
    // Combine branches and endpoints into a single branches array for backend
    const allBranches = [];

    // Add regular branches (with targetTaskId) and branch → gateway targets.
    (g.branches || []).forEach((b) => {
      const rawTarget = b.targetTaskId;
      const isGatewayTarget =
        typeof rawTarget === "string" && rawTarget.startsWith("__GW__");
      if (isGatewayTarget) {
        // Branch flows directly into another gateway; resolve its local id to the
        // gateway's array position (backend links it as target_gateway_id).
        const ref = rawTarget.slice("__GW__".length);
        const gwIdx = (gateways || []).findIndex(
          (x) => String(x.id) === String(ref),
        );
        allBranches.push({
          targetTaskId: null,
          targetGatewayIndex: gwIdx >= 0 ? gwIdx : undefined,
          condition: b.condition || undefined,
          isDefault: b.isDefault || undefined,
        });
      } else if (b.targetTaskId || b.condition) {
        allBranches.push({
          targetTaskId: b.targetTaskId ? Number(b.targetTaskId) : null,
          condition: b.condition || undefined,
          isDefault: b.isDefault || undefined,
          connectToEnd:
            b.targetTaskId && !b.isLoopBack
              ? b.connectToEnd !== false
              : undefined,
        });
      }
    });

    // Add endpoints as branches (with endTaskId/endEventName)
    (g.endpoints || []).forEach((e) => {
      allBranches.push({
        endTaskId: e.endTaskId ? Number(e.endTaskId) : null,
        endEventName: e.endEventName || undefined,
        condition: e.condition || undefined,
      });
    });

    // If no branches were added but gateway exists, ensure at least empty array
    const result = {
      type: String(g.type || "EXCLUSIVE").toUpperCase(),
      name: g.name || undefined,
      afterTaskId: toGatewayTaskId(g.afterTaskId),
      branches: allBranches,
    };

    if (isConvergingType(g.type) && !exclusiveSkipsConvergence(g)) {
      // join → split: reference the target gateway by its array position.
      const gwIdx =
        g.convergeAtGatewayId != null && g.convergeAtGatewayId !== ""
          ? (gateways || []).findIndex(
              (x) => String(x.id) === String(g.convergeAtGatewayId),
            )
          : -1;
      result.convergeAtGatewayIndex = gwIdx >= 0 ? gwIdx : undefined;
      const hasGwTarget = result.convergeAtGatewayIndex != null;
      result.convergeToEnd = !hasGwTarget && !!g.convergeToEnd;
      result.convergeAtTaskId =
        !hasGwTarget && !result.convergeToEnd
          ? toConvergeAtTaskId(g.convergeAtTaskId)
          : null;
      result.convergeGatewayName =
        String(g.convergeGatewayName || "").trim() || null;
    }

    return result;
  });
}

// ========== EXISTING APIS ==========

/**
 * Load BPMN XML for a process via GET (returns saved/generated XML).
 * Uses format=xml and supports forcing regeneration.
 * @param {number} processId - The process ID
 * @param {Object} options
 * @param {boolean} options.skipValidation - Skip validation (default true)
 * @param {boolean} options.regenerate - Force fresh XML generation (default false)
 * @returns {Promise<string>} BPMN XML string
 */
export async function loadBpmnXml(
  processId,
  { skipValidation = true, regenerate = false } = {},
) {
  if (!processId) throw new Error("Process ID is required");
  const queryParts = ["format=xml"];
  if (skipValidation) queryParts.push("skipValidation=true");
  if (regenerate) queryParts.push("regenerate=true");
  const query = queryParts.length ? `?${queryParts.join("&")}` : "";
  const endpoint = `/bpmn-native/generate/${processId}${query}`;
  return api.request(endpoint, { method: "GET" });
}

// Fetch a BPMN PNG for a given process and return an object URL
export async function getBpmnPng(
  processId,
  { width, skipValidation, regenerate = false } = {},
) {
  if (!processId) throw new Error("Process ID is required");
  const queryParts = ["format=png"];
  if (width) queryParts.push(`width=${encodeURIComponent(width)}`);
  if (skipValidation) queryParts.push("skipValidation=true");
  if (regenerate) queryParts.push("regenerate=true");
  const query = queryParts.length ? `?${queryParts.join("&")}` : "";
  const endpoint = `/bpmn-native/generate/${processId}${query}`;
  const blob = await api.request(endpoint, {
    method: "GET",
    responseType: "blob",
    suppressErrorPage: true,
  });
  return URL.createObjectURL(blob);
}

/**
 * Query params for pinning a diagram request to a specific optimization scenario
 * (from the optimize response's `scenarios[].bpmn_spec`) — the SAME contract
 * `analyzeProcess` already uses for the request body. `exclude_task_ids` being an
 * array (even empty) is meaningful to the backend: PRESENT + empty means
 * "eliminate nothing", while OMITTING the param means "auto-detect NVA
 * elimination yourself". Without forwarding this, every "Optimized"/"Analyzed"
 * diagram fetch used the backend's own auto-detect default regardless of which
 * scenario card the analyst had selected — e.g. "Resources only" (which should
 * leave the structure unchanged) could still render with a step eliminated,
 * because the elimination came from auto-detect, not from the active scenario.
 */
function scenarioQueryParts(scenarioSpec) {
  const parts = [];
  if (scenarioSpec && Array.isArray(scenarioSpec.exclude_task_ids)) {
    parts.push(
      `excludeTaskIds=${encodeURIComponent(scenarioSpec.exclude_task_ids.join(","))}`,
    );
  }
  if (scenarioSpec && scenarioSpec.include_parallel !== undefined) {
    parts.push(`includeParallel=${scenarioSpec.include_parallel ? "true" : "false"}`);
  }
  return parts;
}

/**
 * Query param for an ad-hoc RESPONSIBLE-role reassignment (the RACI reassignment
 * picker on the Analyze tab) — "taskId:jobId,taskId:jobId". A what-if change:
 * moves the task's swim lane on the Optimized diagram without touching any
 * stored process_task/job_task row, so it disappears the moment the override is
 * cleared and is never applied unless the analyst also clicks Save.
 */
function jobOverrideQueryParts(jobOverrides) {
  const pairs = (Array.isArray(jobOverrides) ? jobOverrides : [])
    .filter((o) => o && o.task_id != null && o.job_id != null)
    .map((o) => `${o.task_id}:${o.job_id}`);
  if (!pairs.length) return [];
  return [`jobOverrides=${encodeURIComponent(pairs.join(","))}`];
}

// Fetch a BPMN PNG with AI optimization enabled and return an object URL
export async function getOptimizedBpmnPng(
  processId,
  { width, skipValidation, scenarioSpec, jobOverrides } = {},
) {
  if (!processId) throw new Error("Process ID is required");
  const queryParts = [
    "optimize=true",
    ...scenarioQueryParts(scenarioSpec),
    ...jobOverrideQueryParts(jobOverrides),
  ];
  if (width) queryParts.push(`width=${encodeURIComponent(width)}`);
  if (skipValidation) queryParts.push("skipValidation=true");
  const query = `?${queryParts.join("&")}`;
  const endpoint = `/bpmn-native/generate/${processId}${query}`;
  const blob = await api.request(endpoint, {
    method: "GET",
    responseType: "blob",
    suppressErrorPage: true,
  });
  return URL.createObjectURL(blob);
}

/**
 * Fetch BPMN with gateway configuration
 * @param {number} processId - The process ID
 * @param {Object} options - Options including gateways and format settings
 * @param {Array} options.gateways - Array of gateway configurations
 * @param {number} options.width - Optional image width
 * @param {string} options.format - Output format: 'png', 'svg', 'jpeg', 'xml'
 * @returns {Promise<string>} Object URL for image formats, or string content for svg/xml
 */
export async function getBpmnWithGateways(
  processId,
  { gateways = [], width, format = "png" } = {},
) {
  if (!processId) throw new Error("Process ID is required");

  const queryParts = [`format=${format}`];
  if (width) queryParts.push(`width=${encodeURIComponent(width)}`);

  const query = `?${queryParts.join("&")}`;
  const endpoint = `/bpmn-native/generate/${processId}${query}`;

  // For svg/xml formats, the API service will return text automatically
  // since content-type will be text/xml or image/svg+xml
  if (format === "svg" || format === "xml") {
    const response = await api.request(endpoint, {
      method: "POST",
      body: JSON.stringify({ gateways }),
      headers: { "Content-Type": "application/json" },
    });
    return response;
  }

  // For image formats (png, jpeg), return blob URL
  const blob = await api.request(endpoint, {
    method: "POST",
    body: JSON.stringify({ gateways }),
    headers: { "Content-Type": "application/json" },
    responseType: "blob",
  });
  return URL.createObjectURL(blob);
}

/**
 * Fetch tasks for a specific process (includes subprocess workflow rows).
 * Backend may return isSubProcess + childProcessName; keep those fields so
 * gateway placement/branch dropdowns can label and select them. Their taskId
 * is submitted as afterTaskId/targetTaskId like a normal task.
 * @param {number} processId - The process ID
 * @returns {Promise<Array>} Process tasks / subprocess elements
 */
export async function getBpmnTasks(processId) {
  if (!processId) throw new Error("Process ID is required");
  const res = await api.get(`/bpmn-editor/process/${processId}/tasks`);
  const list = Array.isArray(res)
    ? res
    : Array.isArray(res?.tasks)
      ? res.tasks
      : Array.isArray(res?.data)
        ? res.data
        : [];
  // Do not filter by type — subprocess entries must pass through.
  return list.map((t) => ({
    ...t,
    taskId: t.taskId ?? t.task_id ?? t.id,
    taskName: t.taskName ?? t.task_name ?? t.name,
    isSubProcess: !!(
      t.isSubProcess ??
      t.is_sub_process ??
      t.isChildProcess ??
      t.is_child_process
    ),
    childProcessName:
      t.childProcessName ||
      t.child_process_name ||
      t.process_name ||
      t.processName ||
      null,
    order: t.order ?? 0,
  }));
}

/**
 * Debug endpoint to view parsed gateway data
 * @param {number} processId - The process ID
 * @returns {Promise<Object>} Debug information
 */
export async function getBpmnDebug(processId) {
  if (!processId) throw new Error("Process ID is required");
  return api.get(`/bpmn-native/debug/${processId}`);
}

// ========== NEW APIS (from BPMN Editor Guide) ==========

/**
 * Generate SVG preview for live editing
 * Returns SVG content directly as a string for embedding in the UI
 * @param {number} processId - The process ID
 * @param {Array} gateways - Array of gateway configurations
 * @returns {Promise<string>} SVG content as string
 */
export async function getBpmnSvgPreview(processId, gateways = []) {
  if (!processId) throw new Error("Process ID is required");
  // Transform gateways to clean backend format before sending
  const cleanGateways = transformGatewaysForSave(gateways);
  return getBpmnWithGateways(processId, {
    gateways: cleanGateways,
    format: "svg",
  });
}

/**
 * Generate BPMN XML for download or external use
 * @param {number} processId - The process ID
 * @param {Array} gateways - Array of gateway configurations
 * @returns {Promise<string>} BPMN XML content as string
 */
export async function getBpmnXml(processId, gateways = []) {
  if (!processId) throw new Error("Process ID is required");
  // Transform gateways to clean backend format before sending
  const cleanGateways = transformGatewaysForSave(gateways);
  return getBpmnWithGateways(processId, {
    gateways: cleanGateways,
    format: "xml",
  });
}

/**
 * Save the full BPMN XML (including layout/connection changes) back to the backend.
 * The backend stores this so the next load returns the user's exact diagram.
 * @param {number} processId - The process ID
 * @param {string} bpmnXml   - The complete BPMN 2.0 XML string
 * @returns {Promise<Object>} Save result from the backend
 */
export async function saveBpmnXml(processId, bpmnXml) {
  if (!processId) throw new Error("Process ID is required");
  if (!bpmnXml) throw new Error("BPMN XML is required");
  return api.put(`/bpmn-editor/process/${processId}/save-xml`, {
    xml: bpmnXml,
  });
}

/**
 * Check if a process has stored BPMN XML.
 * @param {number} processId - The process ID
 * @returns {Promise<Object>} Stored XML status/details from backend
 */
export async function getStoredBpmnXmlStatus(processId) {
  if (!processId) throw new Error("Process ID is required");
  return api.get(`/bpmn-editor/process/${processId}/save-xml`);
}

/**
 * Clear stored BPMN XML so load falls back to auto-generated layout.
 * @param {number} processId - The process ID
 * @returns {Promise<void|Object>} Backend response
 */
export async function clearStoredBpmnXml(processId) {
  if (!processId) throw new Error("Process ID is required");
  return api.delete(`/bpmn-editor/process/${processId}/save-xml`);
}

/**
 * Validate process flow with gateways
 * Checks for orphan tasks, missing default branches, etc.
 * @param {number} processId - The process ID
 * @returns {Promise<Object>} Validation result with errors and warnings
 */
export async function validateProcessFlow(processId) {
  if (!processId) throw new Error("Process ID is required");
  return api.post(`/bpmn-editor/process/${processId}/validate`);
}

/**
 * Create a new gateway for a process
 * @param {number} processId - The process ID
 * @param {Object} gateway - Gateway configuration
 * @returns {Promise<Object>} Created gateway with ID
 */
export async function createGateway(processId, gateway) {
  if (!processId) throw new Error("Process ID is required");
  if (!gateway) throw new Error("Gateway configuration is required");
  return api.post(
    `/bpmn-editor/process/${processId}/gateways`,
    normalizeGatewayPayload(gateway),
  );
}

/**
 * Update an existing gateway
 * @param {number} gatewayId - The gateway ID
 * @param {Object} gateway - Updated gateway configuration
 * @returns {Promise<Object>} Updated gateway
 */
export async function updateGateway(gatewayId, gateway) {
  if (!gatewayId) throw new Error("Gateway ID is required");
  return api.put(
    `/bpmn-editor/gateways/${gatewayId}`,
    normalizeGatewayPayload(gateway),
  );
}

/**
 * Delete a gateway
 * @param {number} gatewayId - The gateway ID
 * @returns {Promise<void>}
 */
export async function deleteGateway(gatewayId) {
  if (!gatewayId) throw new Error("Gateway ID is required");
  return api.delete(`/bpmn-editor/gateways/${gatewayId}`);
}

/**
 * Load complete process data with relations for the BPMN editor
 * Transforms backend response to frontend-friendly format
 * @param {number} processId - The process ID
 * @returns {Promise<Object>} Process data with tasks and gateways
 */
/**
 * Analyze a BPMN process for soundness violations and advisory findings.
 * Takes 2+ minutes — callers should show a long-running loader.
 *
 * The backend streams a keep-alive heartbeat while the LLM pipeline runs. That
 * commits the response headers up front, so the HTTP status is pinned to 200
 * before the work has actually succeeded or failed. Failures are therefore only
 * reported in the body as `status: "error"` — checking `response.ok` is not
 * enough here and would silently pass errors through as results.
 *
 * @param {number} processId - The process ID
 * @returns {Promise<Object>} Advisory analysis result
 */
export async function analyzeProcess(processId, scenarioSpec) {
  if (!processId) throw new Error("Process ID is required");
  // Optional scenario pin (from the optimize response's scenarios[].bpmn_spec) so the
  // restructured diagram matches exactly what that scenario's other tabs show.
  const hasScenario =
    scenarioSpec &&
    (scenarioSpec.exclude_task_ids !== undefined || scenarioSpec.include_parallel !== undefined);
  const data = await api.request(`/bpmn-native/analyze/${processId}`, {
    method: "POST",
    // 10 min: comfortably above the ~2min+ pipeline, since the heartbeat keeps
    // the connection open and a premature abort would discard finished work.
    timeout: 600_000,
    ...(hasScenario ? { body: JSON.stringify(scenarioSpec) } : {}),
  });

  if (data?.status === "error") {
    throw new Error(data.message || data.error || "Analysis failed. Please try again.");
  }

  return data;
}

/**
 * Fetch a process's BPMN XML for the interactive viewer.
 * @param {number} processId - The process ID
 * @param {Object} options - { optimize: true → the agent's optimized TO-BE structure,
 *   scenarioSpec → pin to a specific scenario's bpmn_spec (see scenarioQueryParts) }
 * @returns {Promise<string>} BPMN 2.0 XML
 */
export async function getProcessBpmnXml(processId, { optimize, scenarioSpec, jobOverrides } = {}) {
  if (!processId) throw new Error("Process ID is required");
  const queryParts = ["format=xml", "skipValidation=true"];
  if (optimize) {
    queryParts.push(
      "optimize=true",
      ...scenarioQueryParts(scenarioSpec),
      ...jobOverrideQueryParts(jobOverrides),
    );
  }
  const query = `?${queryParts.join("&")}`;
  return api.request(`/bpmn-native/generate/${processId}${query}`, {
    method: "GET",
    responseType: "text",
    suppressErrorPage: true,
  });
}

/**
 * Fetch the RESTRUCTURED (advisory-corrected) BPMN XML for the interactive viewer.
 * Requires a prior analyze() call — the backend serves the cached structure.
 * @param {number} processId - The process ID
 * @returns {Promise<string>} BPMN 2.0 XML
 */
export async function getRestructuredBpmnXml(processId) {
  if (!processId) throw new Error("Process ID is required");
  return api.request(`/bpmn-native/restructure/${processId}?format=xml`, {
    method: "POST",
    responseType: "text",
    suppressErrorPage: true,
  });
}

/**
 * Restructure a BPMN process (auto-fix advisory findings) and return a PNG image.
 * @param {number} processId - The process ID
 * @returns {Promise<string>} Object URL for the restructured BPMN PNG
 */
export async function restructureProcess(processId) {
  if (!processId) throw new Error("Process ID is required");
  const blob = await api.request(
    `/bpmn-native/restructure/${processId}?format=png`,
    {
      method: "POST",
      responseType: "blob",
      suppressErrorPage: true,
    },
  );
  return URL.createObjectURL(blob);
}

export async function loadProcessForEditor(processId) {
  if (!processId) throw new Error("Process ID is required");

  const data = await api.get(`/process/${processId}`);

  return {
    processId: data.process_id,
    processCode: data.process_code,
    processName: data.process_name,
    processOverview: data.process_overview,
    companyId: data.company_id,
    processTasks: (data.process_task || []).map((pt) => ({
      processTaskId: pt.process_task_id,
      taskId: pt.task_id,
      order: pt.order,
      task: {
        taskId: pt.task?.task_id,
        taskName: pt.task?.task_name,
        taskCode: pt.task?.task_code,
        order: pt.order,
        jobName: pt.task?.job?.job_name,
        functionName: pt.task?.function?.function_name,
      },
    })),
    gateways: (data.gateways || []).map((g) => {
      const { branches, endpoints } = splitBranchesAndEndpoints(g.branches);
      return {
        id: g.gateway_pk_id,
        type: g.gateway_type ?? g.type,
        name: g.name,
        // A gateway placed after another gateway (after_gateway_id) surfaces in the
        // placement dropdown as "__GW__<pk>" (gateway ids are pks on load).
        afterTaskId:
          (g.afterGatewayId ?? g.after_gateway_id) != null
            ? `__GW__${g.afterGatewayId ?? g.after_gateway_id}`
            : (g.afterTaskId ?? g.after_task_id),
        convergeAtTaskId: (() => {
          const raw = g.convergeAtTaskId ?? g.converge_at_task_id;
          if (raw == null || raw === "" || Number(raw) === 0) return "";
          return String(raw);
        })(),
        convergeGatewayName:
          g.convergeGatewayName ?? g.converge_gateway_name ?? "",
        convergeToEnd: g.convergeToEnd ?? g.converge_to_end ?? false,
        // Loaded gateways use gateway_pk_id as their id, and converge_at_gateway_id
        // is a pk, so the reference carries over directly.
        convergeAtGatewayId:
          g.convergeAtGatewayId ?? g.converge_at_gateway_id ?? "",
        branches,
        endpoints,
      };
    }),
  };
}
